import { useMemo } from "react";
import {
  getCheckInPeriodForConfig,
  isPulseNudgeDue,
  type PulsePeriod,
  type PulsePeriodSummary,
} from "@graftus/schema";
import { useRemoteConfigBoolean } from "@/context/RemoteConfigContext";
import { usePulseCompletion, usePulseConfig } from "@/hooks/queries/use-pulse";
import { useIntelligenceScope } from "@/hooks/queries/use-intelligence-scope";
import { hasProductAccessWithFallback } from "@/utils/productAccess";
import type { BusinessData } from "@/types/businessData";
import type { MembershipRole } from "@graftus/schema";

export type PulseCompletionWithFrequency = PulsePeriodSummary & {
  checkInFrequencyDays?: number;
  checkInPeriod: PulsePeriod;
};

function canConfigurePulse(memberRole: MembershipRole | null | undefined) {
  return memberRole === "owner" || memberRole === "admin";
}

export function usePulseCheckInNudge(
  business: BusinessData | null | undefined,
  memberRole?: MembershipRole | null,
) {
  const businessId = business?.uid;
  const intelligenceEnabled =
    business != null &&
    hasProductAccessWithFallback(business, "intelligence", Date.now());
  const showPulseCheck = useRemoteConfigBoolean("show_pulse_check");
  const enabled = Boolean(
    intelligenceEnabled && showPulseCheck && businessId,
  );

  const configQuery = usePulseConfig(enabled ? businessId : undefined);
  const scopeQuery = useIntelligenceScope(enabled ? businessId : undefined);

  const config = configQuery.data?.config;
  const bootstrapCompleted = configQuery.data?.bootstrapCompleted ?? false;

  const checkInPeriod = useMemo(() => {
    if (!config) return null;
    return getCheckInPeriodForConfig(config, new Date(), bootstrapCompleted);
  }, [config, bootstrapCompleted]);

  const completionQuery = usePulseCompletion(
    enabled && config && checkInPeriod ? businessId : undefined,
    checkInPeriod?.periodStart,
    checkInPeriod?.periodEnd,
  );

  const summary = completionQuery.data as
    | (PulsePeriodSummary & { checkInFrequencyDays?: number })
    | undefined;

  const isConfigured = configQuery.data?.isConfigured ?? false;

  const showSetup = useMemo(() => {
    if (!enabled) return false;
    if (configQuery.isPending || configQuery.isError) return false;
    if (isConfigured) return false;
    return canConfigurePulse(memberRole);
  }, [enabled, configQuery.isPending, configQuery.isError, isConfigured, memberRole]);

  const show = useMemo(() => {
    if (!enabled) return false;
    if (!isConfigured) return false;
    if (!scopeQuery.data?.canSubmitPulse) return false;
    if (!summary || !checkInPeriod || summary.totalMembers <= 0) return false;
    return isPulseNudgeDue(summary.status, checkInPeriod, new Date());
  }, [
    enabled,
    isConfigured,
    scopeQuery.data?.canSubmitPulse,
    summary,
    checkInPeriod,
  ]);

  const isLoading =
    enabled &&
    (configQuery.isPending ||
      (isConfigured &&
        (scopeQuery.isPending || completionQuery.isPending)));

  return {
    show,
    showSetup,
    summary:
      show && summary && checkInPeriod
        ? { ...summary, checkInPeriod }
        : null,
    checkInPeriod,
    isLoading,
    intelligenceEnabled,
    showPulseCheck,
  };
}
